import { useMemo } from "react";
import type { FinancialStatementBlock, StatementLine } from "../contract/types";
import { Blocks } from "./Blocks";
import { PresetShell } from "./PresetShell";
import {
  asRecord,
  compactBlocks,
  createAtom,
  readArray,
  readObjects,
  readString,
  readStringArray,
} from "./data";
import type { PresetViewProps } from "./CourseOverview";

/**
 * Caso del curso (MicroTech, TechGrow…): contexto, anexos, preguntas guía y
 * qué se entrega.
 *
 * `statements` puede venir como lista de nombres ("balance_sheet") o como
 * estados ya armados. Con nombres se muestran como fichas de lo que trae el
 * caso; con datos se pintan los estados financieros de verdad. Sin datos no se
 * inventa un balance.
 */

const STATEMENT_LABEL: Record<string, string> = {
  balance_sheet: "Balance general",
  income_statement: "Estado de resultados",
  cash_flow: "Estado de flujo de efectivo",
};

function statementLabel(value: string): string {
  return STATEMENT_LABEL[value] ?? value;
}

export function CaseStudy({ preset, data, emit, interactive }: PresetViewProps) {
  const company = readString(data, "company") ?? preset.title ?? "Caso";
  const module = readString(data, "module");
  const unit = readString(data, "unit");
  const context = readString(data, "context");
  const deliverable = readString(data, "deliverable");
  const questions = readStringArray(data, "questions");

  const statements = useMemo(() => {
    const raw = readArray(data, "statements");
    const named: string[] = [];
    const structured: { id: string; statement: FinancialStatementBlock["statement"]; periodos: string[]; lines: StatementLine[] }[] = [];

    raw.forEach((entry, index) => {
      if (typeof entry === "string") {
        named.push(entry);
        return;
      }
      const record = asRecord(entry);
      if (!record) return;
      const kind = readString(record, "statement");
      const lines = readObjects(record, "lines")
        .map((line, lineIndex) => {
          const values = readArray(line, "values").map((value) =>
            typeof value === "string" || value === null ? value : String(value),
          );
          const lineKind = readString(line, "kind");
          return {
            label: readString(line, "label") ?? `Línea ${lineIndex + 1}`,
            kind:
              lineKind === "group" || lineKind === "line" || lineKind === "subtotal" || lineKind === "total"
                ? lineKind
                : "line",
            values,
          } satisfies StatementLine;
        });
      if (lines.length === 0) return;
      structured.push({
        id: readString(record, "id") ?? `case_statement_${index}`,
        statement:
          kind === "income_statement" || kind === "cash_flow" ? kind : "balance_sheet",
        periodos: readStringArray(record, "periods"),
        lines,
      });
    });

    return { named, structured };
  }, [data]);

  const blocks = compactBlocks([
    context
      ? createAtom("prose", {
          markdown: context,
        })
      : null,

    ...statements.structured.map((statement) =>
      createAtom("financial_statement", {
        statement: statement.statement,
        periods: statement.periodos.length > 0 ? statement.periodos : ["Periodo 1", "Periodo 2"],
        lines: statement.lines,
        currency: "miles de S/.",
      }),
    ),

    statements.named.length > 0
      ? createAtom("callout", {
          variant: "note",
          title: "Anexos del caso",
          body: `El enunciado incluye: ${statements.named.map(statementLabel).join(", ")}. Trabaja sobre el material del caso antes de pedir el análisis.`,
        })
      : null,

    questions.length > 0
      ? createAtom("prose", {
          meta: { title: "Preguntas guía" },
          markdown: questions.map((question, index) => `${index + 1}. ${question}`).join("\n"),
        })
      : null,

    deliverable
      ? createAtom("callout", {
          variant: "tip",
          title: "Qué se entrega",
          body: deliverable,
        })
      : null,
  ]);

  return (
    <PresetShell
      eyebrow={[unit, module].filter(Boolean).join(" · ") || "Caso"}
      title={preset.title ?? company}
      lead={company !== preset.title ? company : null}
      chips={[
        ...(module ? [{ icon: "layers" as const, label: module }] : []),
        ...(questions.length > 0
          ? [{ icon: "list" as const, label: `${questions.length} preguntas guía` }]
          : []),
        ...(statements.named.length > 0
          ? [{ icon: "table" as const, label: `${statements.named.length} anexos` }]
          : []),
      ]}
    >
      <Blocks blocks={blocks} emit={emit} interactive={interactive} />
    </PresetShell>
  );
}

export default CaseStudy;
